'use client';

import React from 'react';
import type { MarkdownGithubRender } from './markdown-renderer';
import { MermaidWithFallback } from '../mermaid-renderer/mermaid-with-fallback';
import { containsGithubUploadUrl, createGithubMarkdownRenderBatch } from './github-markdown-render-batch';
import { MarkdownRenderer } from './markdown-renderer';
import { MERMAID_BLOCK_WITH_CAPTURE_SOURCE } from '@/lib/mermaid/regex';
import { transformMarkdownToSentenceCase } from '@/lib/utils/text-formatting';
import { normalizeWikiSourceLinks } from '@/lib/wiki/source-links';
export type MixedMarkdownMermaidRendererProps = {
  content: string;
  org: string;
  repo: string;
  id?: string;
  className?: string;
  transformHeadingsToSentenceCase?: boolean;
  isPublicView?: boolean;
  githubRender?: MarkdownGithubRender;
  sourceCommitSha?: string | null;
  sourceBranch?: string | null;
  fallbackToRawCodeOnError?: boolean;
  /** Whether bare commit SHAs should link to the commit on GitHub */
  linkifyCommitReferences?: boolean;
};
type MarkdownMermaidPart = {
  type: 'markdown';
  key: string;
  content: string;
} | {
  type: 'mermaid';
  key: string;
  diagramSrc: string;
  rawMermaidCodeBlock: string;
  captionContent: string | null;
};

/**
 * Renders markdown content that may contain Mermaid diagrams.
 * Non-Mermaid text is rendered via MarkdownRenderer for full GFM support.
 * Mermaid blocks are rendered with the ZoomableMermaid component.
 */
export function MixedMarkdownMermaidRenderer({
  content,
  org,
  repo,
  id,
  className,
  transformHeadingsToSentenceCase = false,
  isPublicView = false,
  githubRender,
  sourceCommitSha,
  sourceBranch,
  fallbackToRawCodeOnError = false,
  linkifyCommitReferences = false
}: MixedMarkdownMermaidRendererProps) {
  // Transform headings to sentence case if enabled
  let processedContent = transformHeadingsToSentenceCase ? transformMarkdownToSentenceCase(content) : content;

  // Normalize generated source links as a final client-side fallback.
  if (org && repo) {
    processedContent = normalizeWikiSourceLinks(processedContent, {
      owner: org,
      repo,
      sourceCommitSha,
      sourceBranch
    });
  }
  const parts_0 = React.useMemo(() => {
    // Create a fresh RegExp for each memoized split to prevent retained `lastIndex`
    const mermaidRegex = new RegExp(MERMAID_BLOCK_WITH_CAPTURE_SOURCE, 'g');
    const parts: MarkdownMermaidPart[] = [];
    let lastIndex = 0;
    let match = mermaidRegex.exec(processedContent);
    while (match) {
      // push preceding text
      if (match.index > lastIndex) {
        const textPart = processedContent.substring(lastIndex, match.index);
        parts.push({
          type: 'markdown',
          key: `md-${lastIndex}`,
          content: textPart
        });
      }

      // Extract mermaid diagram
      const diagramSrc = match[1] || '';
      const rawMermaidCodeBlock = match[0];
      const mermaidEndIndex = match.index + match[0].length;

      // Look for caption/sources that immediately follow the mermaid block
      // Pattern: optional newlines, then italic text (caption), then optional "Sources:" line with links
      const remainingContent = processedContent.substring(mermaidEndIndex);

      // More comprehensive regex to capture italic captions and sources
      const captionSourceRegex = /^\n*(?:_[^_\n]+_|\*[^*\n]+\*)(?:\n+Sources?:\s*(?:.+\n?)+)?/;
      const captionMatch = captionSourceRegex.exec(remainingContent);
      let captionContent = null;
      let captionEndIndex = mermaidEndIndex;
      if (captionMatch) {
        captionContent = captionMatch[0];
        captionEndIndex = mermaidEndIndex + captionMatch[0].length;
      } else {
        // If no italic caption found, check for just "Sources:" line
        const sourcesOnlyRegex = /^\n*Sources?:\s*(?:.+\n?)+/;
        const sourcesMatch = sourcesOnlyRegex.exec(remainingContent);
        if (sourcesMatch) {
          captionContent = sourcesMatch[0];
          captionEndIndex = mermaidEndIndex + sourcesMatch[0].length;
        }
      }

      // push mermaid diagram with caption wrapped in fallback component
      parts.push({
        type: 'mermaid',
        key: `mermaid-${match.index}`,
        diagramSrc,
        rawMermaidCodeBlock,
        captionContent
      });
      lastIndex = captionEndIndex;
      match = mermaidRegex.exec(processedContent);
    }

    // trailing text
    if (lastIndex < processedContent.length) {
      const textPart_0 = processedContent.substring(lastIndex);
      parts.push({
        type: 'markdown',
        key: `md-${lastIndex}`,
        content: textPart_0
      });
    }
    return parts;
  }, [processedContent]);
  const githubRenderBatch = React.useMemo(() => {
    if (githubRender !== 'uploads' || isPublicView) {
      return undefined;
    }
    const contents = parts_0.flatMap(part => part.type === 'markdown' ? [part.content] : [part.captionContent ?? '', ...(fallbackToRawCodeOnError ? [part.rawMermaidCodeBlock] : [])]).filter(containsGithubUploadUrl);
    return contents.length > 1 ? createGithubMarkdownRenderBatch({
      contents,
      org,
      repo
    }) : undefined;
  }, [parts_0, org, repo, isPublicView, githubRender, fallbackToRawCodeOnError]);
  React.useEffect(() => () => githubRenderBatch?.abort(), [githubRenderBatch]);
  let markdownPartIndex = 0;
  // Each MarkdownRenderer derives its DOM id from this id, so suffix a running
  // index to keep `markdown-container-${id}` unique across split parts.
  const nextMarkdownId = () => id ? `${id}-${markdownPartIndex++}` : undefined;
  const renderedParts = parts_0.map(part_0 => {
    if (part_0.type === 'markdown') {
      return <MarkdownRenderer key={part_0.key} content={part_0.content} org={org} repo={repo} id={nextMarkdownId()} className={className} isPublicView={isPublicView} githubRender={githubRender} githubRenderBatch={githubRenderBatch} linkifyCommitReferences={linkifyCommitReferences} />;
    }
    return <MermaidWithFallback key={part_0.key} content={part_0.diagramSrc} className="my-6 flex flex-col items-center" fallback={fallbackToRawCodeOnError ? <MarkdownRenderer content={part_0.rawMermaidCodeBlock} org={org} repo={repo} id={nextMarkdownId()} className={className} isPublicView={isPublicView} githubRender={githubRender} githubRenderBatch={githubRenderBatch} linkifyCommitReferences={linkifyCommitReferences} /> : null}>
        {part_0.captionContent && <MarkdownRenderer content={part_0.captionContent} org={org} repo={repo} className="mt-2 text-center" isPublicView={isPublicView} githubRender={githubRender} githubRenderBatch={githubRenderBatch} linkifyCommitReferences={linkifyCommitReferences} />}
      </MermaidWithFallback>;
  });
  return <div className={className}>{renderedParts}</div>;
}